import { useEffect, useState } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { isValidEmail } from "../../utils/email";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const LabourInfo = () => {
  const navigate = useNavigate();
  const itemId = "labour-info";
  const fieldOrder = ["employeeEmail", "date", "employeeName", "employeeSignature"];
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    }
  }, []);

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    if (!accountId) return;
    const existing = localStorage.getItem(`status_${accountId}_${itemId}`) || localStorage.getItem(`status_${itemId}`);
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const statusToPersist = allEmpty ? "incomplete" : (existing === "complete" ? "complete" : "in-progress");
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData]);

  const [emailError, setEmailError] = useState("");

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const ok = isValidEmail(formData.employeeEmail);
    setEmailError(ok ? "" : "Please enter a valid email address");
    if (!ok) return;
    const accountId = localStorage.getItem("accountId");
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const newStatus = allEmpty ? "incomplete" : allFilled ? "complete" : "in-progress";
    const payload = { status: newStatus, formData };
    if (accountId && newStatus === 'complete') {
      axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
        .catch(err => console.error('Failed to save labour-info:', err))
        .finally(() => {
          axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} }).catch(() => {});
          showSuccess("Form completed successfully!");
          navigate('/register');
        });
    } else {
      navigate('/register');
    }
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "employeeEmail") {
      setEmailError(value ? (isValidEmail(value) ? "" : "Please enter a valid email address") : "");
    }
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        <form className="form-card" onSubmit={handleSubmit}>
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
            <h2 className="form-title" style={{ textAlign: "center" }}>
              Ministry of Labour Information Acceptance Sheet
            </h2>
          </header>

          <section className="form-card-content">
            <p>
              I have provided Talent Employment Inc the following email address,
              to receive the following Information documents:
            </p>

            <div className="form-field">
              <label htmlFor="employeeEmail">Employee's Email*</label>
              <input
                id="employeeEmail"
                type="email"
                value={formData.employeeEmail}
                onChange={(e) => handleInputChange("employeeEmail", e.target.value)}
                required
              />
              {emailError && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{emailError}</div>}
            </div>
          </section>

          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="employeeName">Employee's Full Name*</label>
              <input
                id="employeeName"
                type="text"
                value={formData.employeeName}
                onChange={(e) =>
                  handleInputChange("employeeName", e.target.value)
                }
                required
              />
            </div>
          </div>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit} disabled={!!emailError || !formData.employeeEmail}>Submit</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LabourInfo;
